/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";

import { useContext } from "react";

import { useTranslation } from "react-i18next";

import InfoIconWithTooltip from "@/components/IconsWithTooltips/InfoIconWithTooltip/InfoIconWithTooltip";
import OptionalTooltip from "@/components/Tooltip/OptionalTooltip";
import LanguageContext from "@/context/language/LanguageContext";
import { useOverflow } from "@/shared/hooks/useOverflow";
import Card from "@mui/material/Card";
import Typography from "@mui/material/Typography";

import { colors } from "@dotnet/telematics-design-system";

import { creditsObjectType, CreditsStatuses } from "../../../types";

interface LegendSingleCardProps {
  item: creditsObjectType;
  toLg: boolean;
}

const LegendSingleCard = ({ item, toLg }: LegendSingleCardProps) => {
  const { t } = useTranslation();

  const { language } = useContext(LanguageContext);

  const { isOverflowing, measuredRef } = useOverflow([language]);

  const { name, count } = item;

  const getValueColor = () => {
    if (name === CreditsStatuses.Available) {
      return count <= 0 ? colors.textDestructiveRed : colors.green800;
    }

    return colors.textDark;
  };

  return (
    <Card
      elevation={0}
      css={css({
        flex: toLg ? "unset" : "1 1 0",
        minWidth: 0,
        borderRadius: "6px",
        padding: "16px",
        border: `1px solid ${colors.gray100}`
      })}
    >
      <div
        css={css({
          display: "flex",
          flexDirection: "column",
          gap: "4px",
          minWidth: 0
        })}
      >
        <div
          css={css({
            display: "flex",
            alignItems: "center",
            gap: "4px",
            minWidth: 0
          })}
        >
          <OptionalTooltip
            tooltipPlacement="top"
            tooltipTitle={t(`Dashboard##credits statuses##${name}`)}
            isDisabled={!isOverflowing}
          >
            <Typography
              ref={measuredRef}
              variant="body3Medium"
              color={colors.textPlaceholder}
              css={css({
                flex: 1,
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap"
              })}
            >
              {t(`Dashboard##credits statuses##${name}`)}
            </Typography>
          </OptionalTooltip>

          {name === CreditsStatuses.OverDraft && (
            <div css={css({ flexShrink: 0 })}>
              <InfoIconWithTooltip
                title={t("Dashboard##credit overdraft activated", {
                  overDraftAmount: count
                })}
              />
            </div>
          )}
        </div>

        <Typography variant="body1SemiBold" color={getValueColor()}>
          {count}
        </Typography>
      </div>
    </Card>
  );
};

export default LegendSingleCard;
